import React, { useRef, useEffect, useState, FC } from 'react'

interface TextEditorOptionsProps {
  value?: string
  placeholder?: string
  onChange?: (html: string) => void
  onBlur?: () => void
  minHeight?: number
}

const TextEditorOptions: FC<TextEditorOptionsProps> = ({
  value = '',
  placeholder = '',
  onChange,
  onBlur,
  minHeight = 80,
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value
    }
  }, [value])

  const [boldActive, setBoldActive] = useState(false)
  const [italicActive, setItalicActive] = useState(false)
  const [underlineActive, setUnderlineActive] = useState(false)
  const [listActive, setListActive] = useState(false)

  const updateFormatState = () => {
    try {
      setBoldActive(document.queryCommandState('bold'))
      setItalicActive(document.queryCommandState('italic'))
      setUnderlineActive(document.queryCommandState('underline'))
      setListActive(document.queryCommandState('insertUnorderedList'))
    } catch (e) {
    }
  }

  const handleCommand = (command: string) => {
    document.execCommand(command, false)
    updateFormatState()
        const newHtml = ref.current?.innerHTML || ''
    onChange?.(newHtml)
    
    ref.current?.focus()
  }

  return (
    <div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginBottom: 6 }}>
        <button 
          type="button" 
          onMouseDown={(e) => {
            e.preventDefault() 
            handleCommand('bold')
          }}
          style={{
            padding: 4,
            borderRadius: 4,
            background: boldActive ? '#EDF2FF' : 'transparent',
            border: boldActive ? '1px solid #C7D2FE' : '1px solid transparent',
            cursor: 'pointer',
          }}
        >
          <b>B</b>
        </button>
        <button 
          type="button" 
          onMouseDown={(e) => {
            e.preventDefault() 
            handleCommand('italic')
          }}
          style={{
            padding: 4,
            borderRadius: 4,
            background: italicActive ? '#EDF2FF' : 'transparent',
            border: italicActive ? '1px solid #C7D2FE' : '1px solid transparent',
            cursor: 'pointer',
          }}
        >
          <i>I</i>
        </button>
        <button 
          type="button" 
          onMouseDown={(e) => {
            e.preventDefault() 
            handleCommand('underline')
          }}
          style={{
            padding: 4,
            borderRadius: 4,
            background: underlineActive ? '#EDF2FF' : 'transparent',
            border: underlineActive ? '1px solid #C7D2FE' : '1px solid transparent',
            cursor: 'pointer',
          }}
        >
          <u>U</u>
        </button>
        <button 
          type="button" 
          onMouseDown={(e) => {
            e.preventDefault() 
            handleCommand('insertUnorderedList')
          }}
          style={{
            padding: 4,
            borderRadius: 4,
            background: listActive ? '#EDF2FF' : 'transparent',
            border: listActive ? '1px solid #C7D2FE' : '1px solid transparent',
            cursor: 'pointer',
          }}
        >
          •
        </button>
      </div>
      
      <div style={{ position: 'relative' }}>
        {/* Placeholder shown when empty and not focused */}
        {!value && !isFocused && (
          <div style={{ 
            position: 'absolute', 
            top: 8, 
            left: 8, 
            color: '#9CA3AF',
            pointerEvents: 'none'
          }}>
            {placeholder}
          </div>
        )}
        
        {/* Editor */}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={() => {
            onChange?.(ref.current?.innerHTML || '')
            updateFormatState()
          }}
          onFocus={() => {
            setIsFocused(true)
            updateFormatState()
          }}
          onBlur={() => {
            setIsFocused(false)
            onBlur?.()
          }}
          onMouseUp={updateFormatState}
          onKeyUp={updateFormatState}
          style={{
            minHeight,
            padding: 8,
            border: 'none',
            outline: 'none',
            whiteSpace: 'pre-wrap',
            color: '#1A202C', 
          }}
        />
      </div>
    </div>
  )
}

export default TextEditorOptions